import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, CalendarCheck, Clock3, Tag, UserCheck, ChevronDown } from "lucide-react";
import { IMAGES, PHONE_TEL, WA_LINK } from "../../lib/site";
import { useLang } from "../../lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay, className = "" }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className={`block ${className}`}
            initial={{ y: "115%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const USP_ICONS = [Clock3, Tag, UserCheck];

export const Hero = () => {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.02, 1.12]);
    const fade = useTransform(scrollYProgress, [0.6, 0.85], [1, 0]);
    const lynkOpacity = useTransform(scrollYProgress, [0.3, 0.55], [0, 1]);
    const passatOpacity = useTransform(scrollYProgress, [0.3, 0.55], [1, 0.15]);

    return (
        <section id="home" ref={ref} data-testid="hero-section" className="grain relative h-[190svh]">
            <div className="sticky top-0 flex h-[100svh] flex-col overflow-hidden">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
                <motion.img
                    style={{ opacity: passatOpacity }}
                    src={IMAGES.passat}
                    alt="De zwarte Volkswagen Passat Variant van Taxi Drunnen (GZ-839-F)"
                    className="absolute inset-0 h-full w-full object-cover object-[50%_55%] brightness-[0.75] saturate-[0.9]"
                    data-testid="hero-image"
                />
                <motion.img
                    style={{ opacity: lynkOpacity }}
                    src={IMAGES.lynk}
                    alt="De zwarte Lynk & Co 01 van Taxi Drunnen (JBT-88-B)"
                    className="absolute inset-0 h-full w-full object-cover object-[50%_45%] brightness-[0.75] saturate-[0.9]"
                    data-testid="hero-image-lynk"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-night-950 via-night-950/55 to-night-950/30" />
            <div className="absolute inset-0 bg-gradient-to-r from-night-950/80 via-night-950/20 to-transparent" />

            <motion.div style={{ opacity: fade }} className="container-x relative z-10 flex flex-1 flex-col justify-end pb-14 pt-32 sm:pb-20">
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.15, ease: EASE }}
                    className="mb-6 flex items-center gap-3"
                >
                    <span className="relative flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-400" />
                    </span>
                    <span className="eyebrow" data-testid="hero-eyebrow">{t.hero.eyebrow}</span>
                </motion.div>

                <h1 className="font-display font-wide text-[10.5vw] font-black uppercase leading-[0.98] tracking-tight text-white sm:text-7xl lg:text-8xl" data-testid="hero-title">
                    <MaskedLine delay={0.3}>{t.hero.line1}</MaskedLine>
                    <MaskedLine delay={0.45}>
                        {t.hero.line2a}<span className="text-amber-400">{t.hero.line2b}</span>
                    </MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
                    data-testid="hero-subtitle"
                >
                    {t.hero.sub}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
                    className="mt-9 flex flex-wrap items-center gap-3.5"
                >
                    <a href={PHONE_TEL} data-testid="hero-cta-call" className="btn-amber px-7 py-4 text-base">
                        <Phone size={18} /> {t.hero.call}
                    </a>
                    <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp" className="btn-outline px-7 py-4 text-base">
                        <MessageCircle size={18} /> {t.hero.whatsapp}
                    </a>
                    <Link to="/boeken" data-testid="hero-cta-book" className="btn-ghost px-5 py-4 text-base underline decoration-amber-400/50 underline-offset-8">
                        <CalendarCheck size={18} /> {t.hero.book}
                    </Link>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.15 }}
                    className="mt-12 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 backdrop-blur-md sm:grid-cols-3"
                    data-testid="hero-usps"
                >
                    {t.hero.usps.map((usp, i) => {
                        const Icon = USP_ICONS[i];
                        return (
                            <div key={usp.title} className="flex items-center gap-4 bg-night-950/70 px-5 py-4">
                                <Icon size={22} className="shrink-0 text-amber-400" />
                                <div>
                                    <p className="text-sm font-semibold text-white">{usp.title}</p>
                                    <p className="text-xs text-slate-400">{usp.text}</p>
                                </div>
                            </div>
                        );
                    })}
                </motion.div>
            </motion.div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-5 right-6 z-10 hidden md:block"
            >
                <Link to="/diensten" data-testid="hero-scroll-indicator" aria-label={t.hero.scrollLabel} className="text-slate-400 transition-colors hover:text-amber-300">
                    <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }} className="block">
                        <ChevronDown size={26} />
                    </motion.span>
                </Link>
            </motion.div>
            </div>
        </section>
    );
};
